import { X, MessageSquare, ClipboardList } from 'lucide-react';

/**
 * Chrome del drawer condiviso Attività/Nota: header (titolo + tab switcher testuale + chiudi)
 * e footer (Annulla/bottone primario contestuale). Componenti puramente presentazionali,
 * estratti dalla shell per il budget di righe. Il toggle "Mostra descrizione nell'evento
 * Google Calendar" è stato spostato dentro `CalendarSection` (redesign T5), come switch-row
 * del gruppo Calendario.
 *
 * Fonte: LeadStatusUpdateDrawer.tsx:1810-1853 (header), :2516-2550 (footer).
 * Markup di riferimento: MD/features/2026-08-10-drawer-note-ui-ux-redesign-mockup.html
 * (`.dw-head`/`.dw-title`/`.dw-sub`/`.segmented`, viste 1-4).
 */

export interface DrawerHeaderProps {
  tab: 'note' | 'activity';
  onTabChange(tab: 'note' | 'activity'): void;
  /** Nota obbligatoria: nasconde il tab switcher e cambia titolo/sottotitolo. */
  requireNote: boolean;
  /** Tab switcher nascosto anche fuori da requireNote (edit attività). */
  hideTabs: boolean;
  /** Con nota obbligatoria e nota vuota il pulsante di chiusura resta disabilitato (fonte :1849). */
  closeDisabled: boolean;
  isEditingActivity: boolean;
  onRequestClose(): void;
}

const SEGMENTED_BUTTON_BASE =
  'flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-[5px] transition-colors cursor-pointer';
const SEGMENTED_BUTTON_ON =
  'bg-white text-[#37352F] font-semibold border border-[rgba(55,53,47,0.12)] shadow-sm dark:bg-[#2F3437] dark:text-white dark:border-white/10';
const SEGMENTED_BUTTON_OFF =
  'bg-transparent border border-transparent text-[#787774] hover:text-[#37352F] hover:bg-[rgba(55,53,47,0.05)] dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/[0.05]';

function resolveHeaderTitle(tab: 'note' | 'activity', requireNote: boolean, isEditingActivity: boolean) {
  if (requireNote) return { title: 'Completa il cambio di stato', subtitle: 'Aggiungi una nota per registrare il passaggio' };
  if (tab === 'note') return { title: 'Nuova nota', subtitle: null };
  return { title: isEditingActivity ? 'Modifica attività' : 'Nuova attività', subtitle: null };
}

export function DrawerHeader({ tab, onTabChange, requireNote, hideTabs, closeDisabled, isEditingActivity, onRequestClose }: DrawerHeaderProps) {
  const { title, subtitle } = resolveHeaderTitle(tab, requireNote, isEditingActivity);
  const showTabs = !requireNote && !hideTabs;
  return (
    <div className="px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-[#37352F] dark:text-white">{title}</div>
          {subtitle && <div className="text-xs text-[#787774] dark:text-gray-400 mt-0.5">{subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={onRequestClose}
          className="text-gray-500 dark:text-white/70 hover:text-gray-700 dark:hover:text-white transition-colors disabled:text-gray-300 disabled:dark:text-white/30 disabled:cursor-not-allowed"
          disabled={closeDisabled}
          title={closeDisabled ? 'Aggiungi una nota per chiudere' : 'Chiudi'}
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
      {showTabs && (
        <div className="flex gap-0.5 bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] border border-[rgba(55,53,47,0.09)] dark:border-white/10 rounded-md p-0.5 mt-2.5" data-tour="drawer-tabs">
          <button
            type="button"
            onClick={() => onTabChange('note')}
            className={`${SEGMENTED_BUTTON_BASE} ${tab === 'note' ? SEGMENTED_BUTTON_ON : SEGMENTED_BUTTON_OFF}`}
          >
            <MessageSquare className="h-3 w-3" strokeWidth={1.5} />
            Nota
          </button>
          <button
            type="button"
            onClick={() => onTabChange('activity')}
            className={`${SEGMENTED_BUTTON_BASE} ${tab === 'activity' ? SEGMENTED_BUTTON_ON : SEGMENTED_BUTTON_OFF}`}
          >
            <ClipboardList className="h-3 w-3" strokeWidth={1.5} />
            Attività
          </button>
        </div>
      )}
    </div>
  );
}

export interface DrawerFooterProps {
  isSubmitting: boolean;
  primaryLabel: string;
  onCancel(): void;
  onSubmit(e: React.FormEvent): void;
}

export function DrawerFooter({ isSubmitting, primaryLabel, onCancel, onSubmit }: DrawerFooterProps) {
  return (
    <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] px-4 py-3 bg-[#F7F7F5] dark:bg-[#373C3F]">
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] rounded-md transition-colors"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          data-tour="drawer-save"
          className={`px-3 py-1.5 text-xs font-medium border border-transparent rounded-md transition-colors ${
            isSubmitting
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : 'bg-[#37352F] text-white hover:bg-[#2F2E2B] dark:bg-white dark:text-[#37352F] dark:hover:bg-gray-200'
          }`}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

